import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useSelector, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { StoreState } from '../../store';
import { loginByTemporaryPassword, setSocialLoginCallbackURL, UserActionTypes } from './actions';
import { useCookies } from 'react-cookie';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ApolloError } from '@apollo/client/errors';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { CognitoUser } from 'amazon-cognito-identity-js';
import TagManager from 'react-gtm-module';
import {
  clearLocalStorage,
  getUserRoleFromLocalStorage,
  getUserTeamIDFromLocalStorage,
  removeUserTeamIDToLocalStorage,
  setUserRoleToLocalStorage,
  setUserTeamIDToLocalStorage,
} from '../../utils/localStorageSupport';
import {
  useUpdateLastAccessMutation,
  useGetCurrentUserWithPermissionsQuery,
  CurrentUserFragment as User,
  CurrentPermissionListFragment as UserPermissionList,
  CurrentUserWithPermissionListFragment as UserWithPermissionList,
  useOAuthRegisterMutation,
  MyTeamMemberFragment,
  useGetMyTeamMembersQuery,
  UserRole,
  TeamMemberRole,
} from '../../gen/graphql';
import { defaultErrorMessage } from '../../const/ErrorMessage';
import { UserRoleStudents, UserRoleTeams } from '../../const/UserRole';
import { UserPermissions } from '../../const/UserPermission';
import { sessionStorageSupport } from '../../utils/sessionStorageSupport';
import { getApiErrorMessage } from '../../utils/graphqlError';
import { addDate } from '../../utils/Date';

/** ストアのユーザー情報 */
type UserState = StoreState['user'];

/** fetchCurrentUserWithPermissions の戻り値 */
type FetchCurrentUserResult = {
  /** 取得したユーザー情報と権限。取得できなかった場合は undefined */
  list: UserWithPermissionList | undefined;
  /** APIエラーが発生した場合のエラー */
  error?: ApolloError;
};

/** useUser の戻り値 */
export interface UseUser {
  /** ストアのユーザー情報 */
  user: UserState;
  /** 現在選択中のチームの所属情報。未選択・未所属の場合は null */
  team: MyTeamMemberFragment | null;
  /**
   * メールアドレスとパスワードでログインする。
   * @returns 取得したユーザー情報と権限。仮パスワードでのログインの場合は null
   * @throws Cognito 認証に失敗した場合はそのエラー。LMS にユーザーが無い場合は文字列 'ユーザーとして登録されていません。'
   */
  login: (email: string, password: string) => Promise<UserWithPermissionList | null>;
  /** ソーシャルログインを開始する (Cognito の Hosted UI にリダイレクトする) */
  socialLogin: (provider: CognitoHostedUIIdentityProvider, callbackURL?: string) => Promise<void>;
  /**
   * ソーシャルログインのコールバック画面で、Cognito の認証情報から LMS のログイン状態を確立する。
   * LMS に未登録なら登録も行う。
   * @returns 取得したユーザー情報と権限。失敗した場合は undefined (エラーの dispatch とトップページへの遷移を行う)
   */
  callbackOauthLogin: () => Promise<UserWithPermissionList | undefined>;
  /** ログアウトする */
  logout: () => Promise<void>;
  /** 現在のロールを切り替える。リロード後も維持されるよう localStorage にも保存する */
  changeRole: (newRole: UserRole) => void;
  /** 現在のロールを受講生系のロールに切り替える。受講生系の権限を持たない場合は何もしない */
  changeRoleStudent: () => void;
  /** 現在のチームを切り替える。リロード後も維持されるよう localStorage にも保存する */
  changeTeamID: (newTeamID: string) => void;
  /** 現在のチームの選択を解除する */
  removeTeamID: () => void;
  /**
   * ログイン状態を最新化する。
   * - store が未ログインの場合 (リロード直後など): Amplify が保持している Cognito のセッションから認証情報を取得し、
   *   lms-api からアカウント情報 (ユーザー・権限・所属チーム) を取得して store と Cookie に反映する。
   * - store がログイン済みの場合 (画面遷移時など): 権限と所属チームだけ lms-api から取り直し、ロールを決め直す。
   * 完了すると isRoleConfirmed が true になる。
   * Router がマウントした時と、画面遷移時に呼ばれることを想定している。
   */
  refresh: () => Promise<void>;
  /**
   * 処理中かどうか。
   * FIXME: どこからも参照されていない。処理の途中で例外が出た場合に false に戻らない分岐もある。
   */
  isLoadingUser: boolean;
  /**
   * refresh によってロールが確定したかどうか。Router はこれが true になるまで Loader を表示する。
   * フックのインスタンスごとの state なので、refresh を呼んだ Router 以外のコンポーネントから見ても true にはならない。
   */
  isRoleConfirmed: boolean;
  /** Google 認証でログインしたユーザーかどうか (旧 Facebook 認証は含まない) */
  isSocialLoginUser: () => boolean;
  /** コーチング付きの受講コースを持つかどうか */
  hasCoaching: () => boolean;
  /** 現在のロールで機能 (functionType) に対する操作 (permissionType) が許可されているかどうか */
  permissionCheck: (functionType: string, permissionType: string) => boolean;
  /** 講師ロールに切り替えられるかどうか (講師権限を持ち、現在のロールが講師でない) */
  permissionCheckToInstructor: () => boolean;
  /** コーチロールに切り替えられるかどうか (コーチ権限を持ち、現在のロールがコーチでない) */
  permissionCheckToCoach: () => boolean;
  /** 受講生系ロールに切り替えられるかどうか (受講生系の権限を持ち、現在のロールが受講生系でない) */
  permissionCheckToStudent: () => boolean;
  /**
   * APIからユーザー情報と権限を取得し、ストアに反映する。
   * 戻り値は失敗時も必ずオブジェクトで、取得できたかどうかは FetchCurrentUserResult の list の有無で判定すること。
   */
  fetchCurrentUserWithPermissions: () => Promise<FetchCurrentUserResult>;
  /** パスワード再設定を Cognito に依頼し、パスワード変更中の状態にする */
  forgotPassword: (email: string) => Promise<void>;
}

/**
 * ログイン状態・ロール・権限を扱うカスタムフック。
 * ログイン状態やロール・権限を参照・変更する処理は、store や Amplify の Auth を直接触らずに必ずこのフックを経由すること。
 *
 * # 典型的な使い方 - ユーザー情報や権限の取得
 * @example
 * const { user, permissionCheck } = useUser();
 * const name = user.lmsUser?.nickName;
 * if (permissionCheck(FunctionType.Chat, PermissionType.Read)) { ... }
 *
 * # 以降、限定的な使い方
 *
 * ## ログイン
 * - login / socialLogin (メールアドレス+パスワード / Google 認証の開始)
 * - callbackOauthLogin: LoginOauth (Google 認証から戻ってきたコールバック画面)
 * - refresh: Router (マウント時と画面遷移時にログイン状態を最新化する。isRoleConfirmed が true になるまで Router は Loader を表示する)
 *
 * ## ログアウト
 * logout: store、axios、localStorage、Cognito、Cookieをログアウトの状態に更新する。
 *
 * ## ログイン中の状態の変更
 * - ロールの切り替え: changeRole / changeRoleStudent
 * - チームの切り替え: changeTeamID / removeTeamID
 * - プロフィール等を更新して store のユーザー情報を最新化する: fetchCurrentUserWithPermissions()
 */
export const useUser = (): UseUser => {
  const user = useSelector((state: StoreState) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [isRoleConfirmed, setIsRoleConfirmed] = useState(false);

  const { cookies, setCookies } = useTrackingCookies(user);
  const [oAuthRegister] = useOAuthRegisterMutation();
  const { fetchCurrentUserWithPermissions, fetchMyTeamMembers, updateLastAccessedAt } = useUserQueries(
    user,
    dispatch,
    cookies,
  );

  const team = user.teams.find((team) => team.teamID === user.teamID) ?? null;

  const ctx: UseUserContext = {
    user,
    dispatch,
    navigate,
    cookies,
    oAuthRegister,
    setCookies,
    setIsLoadingUser,
    setIsRoleConfirmed,
    fetchCurrentUserWithPermissions,
    fetchMyTeamMembers,
    updateLastAccessedAt,
  };

  const login = useLogin(ctx);
  const callbackOauthLogin = useCallbackOauthLogin(ctx);
  const refresh = useRefresh(ctx);
  const { socialLogin, logout, forgotPassword } = useAuthActions(ctx);
  const { changeRole, changeRoleStudent, changeTeamID, removeTeamID } = useRoleAndTeamActions(user, dispatch);
  const {
    permissionCheck,
    permissionCheckToInstructor,
    permissionCheckToCoach,
    permissionCheckToStudent,
    isSocialLoginUser,
    hasCoaching,
  } = usePermissionChecks(user);

  useGtmUserDataLayer(user);
  useRestoreSocialLoginCallbackURL(dispatch);

  return {
    user,
    team,
    login,
    socialLogin,
    callbackOauthLogin,
    logout,
    changeRole,
    changeRoleStudent,
    changeTeamID,
    removeTeamID,
    refresh,
    isLoadingUser,
    isSocialLoginUser,
    hasCoaching,
    permissionCheck,
    permissionCheckToInstructor,
    permissionCheckToCoach,
    permissionCheckToStudent,
    forgotPassword,
    fetchCurrentUserWithPermissions,
    isRoleConfirmed,
  };
};

/** 内部的に使う値のセット */
type UseUserContext = {
  user: UserState;
  dispatch: Dispatch;
  navigate: NavigateFunction;
  /** Cookie の値 (流入計測用の smrc_ を参照する) */
  cookies: { [name: string]: string | undefined };
  oAuthRegister: ReturnType<typeof useOAuthRegisterMutation>[0];
  setCookies: () => void;
  setIsLoadingUser: (value: boolean) => void;
  setIsRoleConfirmed: (value: boolean) => void;
  fetchCurrentUserWithPermissions: () => Promise<FetchCurrentUserResult>;
  fetchMyTeamMembers: () => Promise<MyTeamMemberFragment[] | undefined>;
  updateLastAccessedAt: () => Promise<void>;
};

/**
 * GTM が参照する Cookie (lmsuid / is_ins / is_std / is_adm / is_sns / sns_type) を扱う。
 * @returns cookies: 現在の Cookie、setCookies: ストアのユーザー情報で Cookie を書き直す関数
 */
function useTrackingCookies(user: UserState) {
  const [cookies, setCookie, removeCookie] = useCookies();

  // GTMで読み込まれてるため削除禁止！
  // FIXME: 依存配列が [user] だけのため、dispatch 直後に呼ぶと dispatch 前の store の値で Cookie を書いてしまう (login / logout / refresh のセッション復元)。
  const setCookies = useCallback(() => {
    // 一度すべてリセットしてから、ログイン中ならユーザー情報で上書きする
    removeCookie('lmsuid', { path: '/', domain: process.env.REACT_APP_COOKIE_DOMAIN });
    setCookie('is_ins', 0, trackingCookieOptions());
    setCookie('is_std', 0, trackingCookieOptions());
    setCookie('is_adm', 0, trackingCookieOptions());
    setCookie('is_sns', 0, trackingCookieOptions());
    setCookie('sns_type', 0, trackingCookieOptions());

    if (user.lmsUser) {
      setCookie('lmsuid', user.lmsUser.id, trackingCookieOptions());
      setCookie('sns_type', user.lmsUser.snsType, trackingCookieOptions());
      setCookie('is_ins', hasInstructorRole(user.permissionList), trackingCookieOptions());
      setCookie('is_std', hasStudentRole(user.permissionList), trackingCookieOptions());
      setCookie('is_adm', hasAdminRole(user.permissionList), trackingCookieOptions());
      if (user.lmsUser.snsType !== 0) setCookie('is_sns', 1, trackingCookieOptions());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return { cookies, setCookies };
}

/** lms-api への問い合わせをまとめたhook(ユーザー情報と権限の取得 / 所属チームの取得 / 最終アクセス日時の更新) */
function useUserQueries(user: UserState, dispatch: Dispatch, cookies: Record<string, unknown>) {
  const [updateLastAccessMutation] = useUpdateLastAccessMutation();

  /** 最終アクセス日時を更新する (完了は待たない) */
  const updateLastAccessedAt = useCallback(async (): Promise<void> => {
    try {
      const smrc = cookies['smrc_'] ? String(cookies['smrc_']) : undefined;
      updateLastAccessMutation({ variables: { smrc: smrc } });
    } catch (e) {
      // GraphQLのエラーは共通のエラーハンドラでSentryに送信しているためここでは握りつぶす
      // FIXME: mutation を await していないため、try/catch は同期エラーしか捕捉できず、Promise の reject は unhandled rejection になる。
      //        待たない設計にするなら .catch() を付けるべき。
      return;
    }
  }, [updateLastAccessMutation, cookies]);

  const { refetch: refetchCurrentUserWithPermissions } = useGetCurrentUserWithPermissionsQuery({
    // await で戻り値を利用するためにSkip：trueを設定してRefetchで運用(LazyQueryだと戻り値が利用できず)
    // see:https://stackoverflow.com/questions/68216866/async-validation-with-uselazyquery-hook
    skip: true,
  });

  /** ユーザー情報と権限を取得してストアに反映する。詳細は UseUser.fetchCurrentUserWithPermissions を参照 */
  const fetchCurrentUserWithPermissions = useCallback(async (): Promise<FetchCurrentUserResult> => {
    return await refetchCurrentUserWithPermissions()
      .then((result) => {
        // onCompletedだと毎回呼ばれないためここでCurrentUserを再設定(notifyOnNetworkStatusChange: true && fetchPolicy: 'network'でも動作せず)
        const currentUserWithPermissions = result.data.currentUserWithPermissions;
        if (!currentUserWithPermissions) return { list: undefined };

        const currentUser = currentUserWithPermissions.user;
        const currentPermissionList = currentUserWithPermissions.permissionList;
        const contractsOfStripeSubscriptionForAnalytics =
          currentUserWithPermissions.contractsOfStripeSubscriptionForAnalytics;
        if (!currentUser || !currentPermissionList) return { list: undefined };

        dispatch({
          type: UserActionTypes.setLMSUser,
          payload: {
            lmsUser: currentUser,
            permissionList: currentPermissionList,
            contractsOfStripeSubscriptionForAnalytics: contractsOfStripeSubscriptionForAnalytics,
            // 課金状況によりroleが変わる(Subscription <=> General)ことがあるため、ユーザー情報と同時にroleも最新化する
            // FIXME: role は取得した権限ではなく store の (古い) teams / lmsUser から算出されるため、未ログイン状態から呼ぶと NotLogin になる。
            //        他の箇所で正しい role を dispatch しているため実害はないが、lmsUser / role を更新している箇所が散らばっているので、まとめて見直したほうが良い。
            role: resolveRoleAndSelectTeam(dispatch, currentPermissionList, user.teams, user.lmsUser?.id),
          },
        });
        // FIXME: currentUserWithPermissions が無い場合は上で return 済みのため、?? undefined は到達しない。
        return { list: currentUserWithPermissions ?? undefined };
      })
      .catch((err) => {
        // FIXME: 失敗時も必ずオブジェクトを返すため、RegisterFreemium の `if (!user) return;` のような戻り値の truthy 判定は機能しない。
        //        呼び出し元は list の有無で判定すること。
        return { list: undefined, error: err };
      });
  }, [dispatch, refetchCurrentUserWithPermissions, user.lmsUser?.id, user.teams]);

  const { refetch: refetchMyTeamMembers } = useGetMyTeamMembersQuery({
    // LazyQueryだと前回ログインユーザーの情報が取得されてしまうため、Skip：trueを設定してRefetchで運用
    skip: true,
  });

  /**
   * 所属チームを取得してストアに反映する。最後のチームを選択中のチームにする。
   * @returns 所属チーム。未所属の場合は undefined
   */
  // FIXME: catch が無く、APIエラーはそのまま呼び出し元へ伝播する (呼び出し元も catch していない)。
  const fetchMyTeamMembers = useCallback(async (): Promise<MyTeamMemberFragment[] | undefined> => {
    return await refetchMyTeamMembers().then((result) => {
      // onCompletedだと毎回呼ばれないためここでteamを再設定(notifyOnNetworkStatusChange: true && fetchPolicy: 'network'でも動作せず)
      // Queryのdataが取得されないため取得情報をstoreに格納
      const myTeams = result.data.myTeamMembers;
      if (!myTeams || myTeams.length === 0) {
        dispatch({
          type: UserActionTypes.setTeams,
          payload: {
            teamID: null,
            teams: [],
          },
        });
        return;
      }

      const myTeamID = myTeams[myTeams.length - 1].teamID;
      dispatch({
        type: UserActionTypes.setTeams,
        payload: {
          teamID: myTeamID,
          teams: myTeams,
        },
      });
      return myTeams;
    });
  }, [dispatch, refetchMyTeamMembers]);

  return { fetchCurrentUserWithPermissions, fetchMyTeamMembers, updateLastAccessedAt };
}

/** メールアドレスとパスワードでログインする */
function useLogin(ctx: UseUserContext): UseUser['login'] {
  const {
    dispatch,
    setCookies,
    setIsLoadingUser,
    fetchCurrentUserWithPermissions,
    fetchMyTeamMembers,
    updateLastAccessedAt,
  } = ctx;

  return async (email: string, password: string): Promise<UserWithPermissionList | null> => {
    setIsLoadingUser(true);

    Auth.configure({
      Auth: {
        region: process.env.REACT_APP_COGNITO_REGION,
        userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
        userPoolWebClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
      },
      oauth: {
        domain: process.env.REACT_APP_COGNITO_CLIENT_DOMAIN,
        redirectSignIn: process.env.REACT_APP_DOMAIN + '/login/oauth/callback',
        redirectSignOut: process.env.REACT_APP_DOMAIN + '/',
        responseType: 'code',
      },
    });

    // FIXME: Auth.signIn が reject した場合を catch しておらず、isLoadingUser が true のまま呼び出し元へ伝播する。
    const cognitoUserRes = await Auth.signIn(email, password);
    // eqeqeqの導入時点で存在していたコードなので、そのままにしておく。
    // eslint-disable-next-line eqeqeq
    if (cognitoUserRes == undefined) {
      setIsLoadingUser(false);
      // FIXME: Sentry にメールアドレス(個人情報)をそのまま送っている。
      Sentry.captureException('cognitoUserRes is undefined. Email: ' + email);
      // FIXME: Error ではなく文字列を throw している (スタックトレースが取れず、呼び出し元は文字列比較で判定している)。
      throw 'ユーザーとして登録されていません。';
    }

    if (cognitoUserRes.challengeName === 'NEW_PASSWORD_REQUIRED') {
      dispatch(
        loginByTemporaryPassword({
          cognitoUser: cognitoUserRes,
        }),
      );

      setIsLoadingUser(false);
      return null;
    }

    // FIXME: NEW_PASSWORD_REQUIRED 以外の challengeName (SMS_MFA 等) が返ると signInUserSession が無く、ここで TypeError になる。
    //        現状 MFA は使用していないため実害はないが、ハンドリングはしておいたほうが良い。
    setAuthorizationHeader(cognitoUserRes.signInUserSession.idToken.jwtToken);

    const { list: currentUserWithPermissionsList } = await fetchCurrentUserWithPermissions();
    const currentUser = currentUserWithPermissionsList?.user;
    const currentPermissionList = currentUserWithPermissionsList?.permissionList;
    const contractsOfStripeSubscriptionForAnalytics =
      currentUserWithPermissionsList?.contractsOfStripeSubscriptionForAnalytics;
    if (!currentUser || !currentPermissionList) {
      // FIXME: 「ユーザー未登録」「レスポンスが空」「APIエラー」を区別せず、すべて「ユーザーとして登録されていません」として扱っている。
      //        fetchCurrentUserWithPermissions は error を返しているが、ここでは捨てている。原因ごとにメッセージを分けるべき。
      // FIXME: Sentry にメールアドレス (個人情報) を送っている。
      //        また、この分岐に入る時点で currentUser / currentPermissionList のどちらかは必ず無いが、
      //        fetchCurrentUserWithPermissions は片方だけが無い場合も list を undefined にするため、JSON.stringify は常に実行されず 'null' 固定になる。
      Sentry.captureException(
        'currentUser or currentPermissionList is null. Email: ' +
          email +
          ' currentUser: ' +
          (currentUser ? JSON.stringify(currentUser, null, 2) : 'null') +
          ' currentPermissionList: ' +
          (currentPermissionList ? JSON.stringify(currentPermissionList, null, 2) : 'null'),
      );
      // FIXME: 文字列を throw している(上記と同じ)。isLoadingUser も true のまま残る。
      throw 'ユーザーとして登録されていません。';
    }

    // FIXME: fetchMyTeamMembers が reject した場合のハンドリングをしておらず、setLMSUser だけ dispatch された中途半端な状態で例外が伝播する。
    const myTeams = await fetchMyTeamMembers();

    const payload = {
      lmsUser: currentUser,
      cognitoUser: cognitoUserRes,
      // FIXME: permissionCheck というキーは store に存在しない (正しくは permissionList)。
      //        fetchCurrentUserWithPermissions の setLMSUser で permissionList は設定済みのため実害はないが、直すこと。
      permissionCheck: currentPermissionList,
      contractsOfStripeSubscriptionForAnalytics: contractsOfStripeSubscriptionForAnalytics,
      role: resolveRoleAndSelectTeam(dispatch, currentPermissionList, myTeams, currentUser.id),
      isChangePassword: false,
      isLogged: true,
      loginError: '',
    };

    dispatch({
      type: UserActionTypes.login,
      payload,
    });

    // FIXME: setCookies はログイン前の store を参照しているため、ここではリセット処理しか行われない。
    //        実際の値は遷移後の refresh で設定されるため実害はないが、直したほうが良い。
    setCookies();
    setIsLoadingUser(false);
    updateLastAccessedAt();

    return currentUserWithPermissionsList;
  };
}

/** ソーシャルログインのコールバック処理 */
function useCallbackOauthLogin(ctx: UseUserContext): UseUser['callbackOauthLogin'] {
  const {
    dispatch,
    navigate,
    cookies,
    oAuthRegister,
    setCookies,
    setIsLoadingUser,
    fetchCurrentUserWithPermissions,
    fetchMyTeamMembers,
    updateLastAccessedAt,
  } = ctx;

  /**
   * ソーシャルログインしたユーザーを LMS に登録する。
   * @returns 登録したユーザー
   * @throws ApolloError (oAuthRegister のエラー)
   */
  const registerSocialLoginUser = async (userName: string): Promise<User | undefined> => {
    const res = await oAuthRegister({
      variables: {
        input: {
          userName: userName,
          smrc: cookies['smrc_'],
        },
      },
    });
    TagManager.dataLayer({
      dataLayer: {
        userId: res.data?.oAuthRegister.id,
        event: 'gtm-register-step_01',
      },
      dataLayerName: 'LMSDataLayer',
    });
    return res.data?.oAuthRegister;
  };

  return async () => {
    /** エラーメッセージを dispatch してトップページへ遷移する */
    const abortOauthLogin = (message: string) => {
      dispatch({
        type: UserActionTypes.loginError,
        payload: { error: message },
      });
      navigate('/');
    };

    const cognitoUserRes = await Auth.currentAuthenticatedUser({ bypassCache: false })
      .then((session) => session)
      .catch(() => {
        // FIXME: ここでエラーを dispatch して遷移しても処理は終わらず、戻り値が undefined になるため直後の分岐にも入る。
        //        結果的に loginError が別のメッセージで上書きされ、navigate('/') も 2 回呼ばれる。
        abortOauthLogin('無効なユーザーです');
        return;
      });
    // eqeqeqの導入時点で存在していたコードなので、そのままにしておく。
    // eslint-disable-next-line eqeqeq
    if (cognitoUserRes == undefined) {
      abortOauthLogin(defaultErrorMessage);
      return;
    }

    setAuthorizationHeader(cognitoUserRes.signInUserSession.idToken.jwtToken);

    // まず既存ユーザーとして取得を試み、取得できなければ新規登録して取り直す。
    // FIXME: 新規登録の場合、この 1 回目の取得は必ず失敗し、API 側で Invalid Token として Sentry に発報される (AB#12910)。
    const { list: currentUserWithPermissionsResOp, error: fetchError } = await fetchCurrentUserWithPermissions();
    // 未登録 (CommonInvalidToken) 以外の理由で取得に失敗した場合 (通信エラーなど) は中断する。
    if (fetchError && fetchError.graphQLErrors[0]?.extensions?.code !== 'CommonInvalidToken') {
      abortOauthLogin(defaultErrorMessage);
      return;
    }

    const currentUserWithPermissionsRes = !currentUserWithPermissionsResOp
      ? await registerSocialLoginUser(cognitoUserRes.username)
          .then(async () => {
            const { list, error } = await fetchCurrentUserWithPermissions();
            if (error || !list) {
              // 登録は成功しているのにユーザー情報が取得できないのは想定外のため、汎用のエラーを表示する
              dispatch({ type: UserActionTypes.loginError, payload: { error: defaultErrorMessage } });
              return;
            }

            sessionStorageSupport.setItem('REGISTERED_TYPE', 'new');
            sessionStorageSupport.setItem('QUESTIONNAIRE', 'true');
            return list;
          })
          .catch((err) => {
            dispatch({
              type: UserActionTypes.loginError,
              payload: {
                error: getApiErrorMessage(err, defaultErrorMessage, {
                  // @see: https://samurai-7s.backlog.com/view/LMS-4645
                  // デフォルトのErrorMessage.tsxのUserAlreadyExistsの文言だと今回のケースでは不親切なので、より親切な文言を表示するように設定する。
                  UserAlreadyExists:
                    // @see: utils/CognitoErrorDictionary.ts (TranslateCognitoError)
                    // FIXME: cognitoのエラー文をここで書かないといけないのはおかしい。直すこと。
                    'If you have already registered an account with an email address, you will not be able to register/login with a new social account with the same email address. Please try logging in with your email address and password.',
                }),
              },
            });
            return;
          })
      : currentUserWithPermissionsResOp;
    const currentUser = currentUserWithPermissionsRes?.user;
    const currentPermissionList = currentUserWithPermissionsRes?.permissionList;
    const contractsOfStripeSubscriptionForAnalytics =
      currentUserWithPermissionsRes?.contractsOfStripeSubscriptionForAnalytics;

    if (!currentUser || !currentPermissionList) {
      navigate('/');
      return;
    }

    // FIXME: API は user を null か完全なオブジェクトで返すため、空オブジェクトになる経路は実質存在しない。
    if (Object.keys(currentUser).length !== 0) {
      // FIXME: fetchMyTeamMembers が reject した場合は catch しておらず、呼び出し元の LoginOauth も catch していないため、
      //        画面遷移が行われずユーザーは /login/oauth/callback の空画面に取り残される。
      const myTeams = await fetchMyTeamMembers();

      const payload = {
        lmsUser: currentUser,
        cognitoUser: cognitoUserRes,
        permissionList: currentPermissionList,
        contractsOfStripeSubscriptionForAnalytics: contractsOfStripeSubscriptionForAnalytics,
        role: resolveRoleAndSelectTeam(dispatch, currentPermissionList, myTeams, currentUser.id),
        isChangePassword: false,
        isLogged: true,
        loginError: '',
      };

      dispatch({
        type: UserActionTypes.login,
        payload,
      });

      // FIXME: この関数では setIsLoadingUser(true) を呼んでいないので、false にする意味がない。
      setIsLoadingUser(false);

      updateLastAccessedAt();
    } else {
      abortOauthLogin(defaultErrorMessage);
      return;
    }

    // FIXME: login と同様、ログイン前の store を参照しているためリセット処理しか行われない。
    setCookies();

    return currentUserWithPermissionsRes;
  };
}

/** ログイン状態を最新化する */
function useRefresh(ctx: UseUserContext): UseUser['refresh'] {
  const {
    user,
    dispatch,
    setCookies,
    setIsLoadingUser,
    setIsRoleConfirmed,
    fetchCurrentUserWithPermissions,
    fetchMyTeamMembers,
    updateLastAccessedAt,
  } = ctx;

  return useCallback(async () => {
    /** 未ログインとしてロールと Cookie をリセットする */
    const resetToNotLogin = () => {
      dispatch({ type: UserActionTypes.changeRole, payload: { role: UserRole.NotLogin } });
      setCookies();
    };

    setIsLoadingUser(true);

    // ログイン済みなら権限とチームだけ取り直してロールを決め直す
    if (user.isLogged) {
      setCookies();
      // FIXME: 一時的な通信エラーで権限が取得できなかった場合も、ログイン中のユーザーのロールが NotLogin に落ちる (store は isLogged のまま)。
      //        エラー内容も捨てられている。取得失敗時は現在のロールを維持するなど、ハンドリングを分けるべき。
      const { list: currentUserWithPermissions } = await fetchCurrentUserWithPermissions();
      const currentPermissionList = currentUserWithPermissions?.permissionList;
      // FIXME: fetchMyTeamMembers が reject した場合は catch しておらず、Router も refresh() を await も catch もしていないため unhandled rejection になる。
      const myTeams = await fetchMyTeamMembers();
      dispatch({
        type: UserActionTypes.changeRole,
        payload: { role: resolveRoleAndSelectTeam(dispatch, currentPermissionList, myTeams, user.lmsUser?.id) },
      });
      setIsRoleConfirmed(true);
      setIsLoadingUser(false);
      return;
    }

    // 未ログインなら Cognito の認証情報からセッションを復元する
    const currentCognitoUser = await Auth.currentAuthenticatedUser()
      .then((res) => res)
      .catch(() => {
        // FIXME: ここで処理は終わらず、戻り値が undefined になるため直後の分岐にも入り、NotLogin の dispatch と Cookie のリセットが 2 回ずつ実行される。
        resetToNotLogin();
        setIsRoleConfirmed(true);
        setIsLoadingUser(false);
        return;
      });

    if (!currentCognitoUser) {
      // FIXME: 上の catch と違い setIsRoleConfirmed(true) が無い。Amplify の currentAuthenticatedUser は未ログイン時に reject するため
      //        この分岐に単独で到達することは実質ないが、到達すると Router が Loader を表示したまま固まる。catch 側と処理を揃えること。
      resetToNotLogin();
      setIsLoadingUser(false);
      return;
    }

    setAuthorizationHeader(currentCognitoUser.signInUserSession.idToken.jwtToken);

    // FIXME: Google で新規登録した直後は Cognito にはユーザーがいるが LMS の usersテーブルに行が無いため、
    //        Router のマウント時に呼ばれるこの取得で API が Invalid Token を返し、API 側で Sentry に発報される (AB#12910 の 1 つ目のアラート)。
    const { list: currentUserWithPermissionsRes } = await fetchCurrentUserWithPermissions();
    const currentUser = currentUserWithPermissionsRes?.user;
    const currentPermissionList = currentUserWithPermissionsRes?.permissionList;
    const contractsOfStripeSubscriptionForAnalytics =
      currentUserWithPermissionsRes?.contractsOfStripeSubscriptionForAnalytics;

    if (!currentUser || !currentPermissionList) {
      resetToNotLogin();
      setIsRoleConfirmed(true);
      setIsLoadingUser(false);
      return;
    }

    // FIXME: role なしの login をここで dispatch する用途が見当たらず、チーム取得後の dispatch と重複している。
    dispatch({
      type: UserActionTypes.login,
      payload: {
        cognitoUser: currentCognitoUser,
        lmsUser: currentUser,
        permissionList: currentPermissionList,
        contractsOfStripeSubscriptionForAnalytics: contractsOfStripeSubscriptionForAnalytics,
      },
    });

    // FIXME: dispatch 直後の store はまだ未ログインのため、リセット処理しか行われない。
    //        実際は isRoleConfirmed の変化で Router が refresh を再実行し、ログイン済みの経路で正しく設定される。
    setCookies();

    // FIXME: fetchMyTeamMembers が reject した場合は catch しておらず、isRoleConfirmed が false のまま残るため、初回ロードでは Router が Loader を表示したまま固まる。
    const myTeams = await fetchMyTeamMembers();

    dispatch({
      type: UserActionTypes.login,
      payload: {
        cognitoUser: currentCognitoUser,
        lmsUser: currentUser,
        permissionList: currentPermissionList,
        contractsOfStripeSubscriptionForAnalytics: contractsOfStripeSubscriptionForAnalytics,
        role: resolveRoleAndSelectTeam(dispatch, currentPermissionList, myTeams, currentUser.id),
      },
    });

    setIsRoleConfirmed(true);
    setIsLoadingUser(false);

    // update access date
    updateLastAccessedAt();

    return;
    // FIXME: 依存配列が [user] だけで exhaustive-deps を無効化しているため、setCookies 等が古いクロージャを参照しうる。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
}

/** Cognito に対する操作 (ソーシャルログインの開始 / ログアウト / パスワード再設定) */
function useAuthActions(ctx: UseUserContext): Pick<UseUser, 'socialLogin' | 'logout' | 'forgotPassword'> {
  const { user, dispatch, setCookies } = ctx;

  /**
   * ソーシャルログイン
   * @param provider 認証プロバイダ (Google)
   * @param callbackURL ソーシャルログイン後に遷移する画面
   */
  const socialLogin = useCallback(
    async (provider: CognitoHostedUIIdentityProvider, callbackURL?: string): Promise<void> => {
      // ソーシャルログイン時のパラメーター引き回し対応
      // TODO: Hubのイベントがステージングで発火しない問題の原因が分かったらcustomStateを使う方を戻す
      // // @see https://docs.amplify.aws/lib/auth/social/q/platform/js/#setup-frontend
      // await Auth.federatedSignIn({ provider, customState: callbackURL });
      if (callbackURL) {
        sessionStorageSupport.setItem('SOCIAL_LOGIN_CALLBACK_URL', callbackURL);
      }
      await Auth.federatedSignIn({ provider });
    },
    [],
  );

  const logout = useCallback(async () => {
    dispatch({
      type: UserActionTypes.logout,
    });

    axios.defaults.headers.common['Authorization'] = '';
    // FIXME: Auth.signOut より先に localStorage.clear() で Amplify の認証情報も消してしまうため、signOut は「ログイン中のユーザー無し」として何もせず終わる。
    //        ソーシャルログインの場合は Cognito 側のセッションを終了するリダイレクト (redirectSignOut) も行われない。
    //        順番を直すとソーシャルログインユーザーはリダイレクトが発生して挙動が変わるため、影響を確認したうえで直すこと。
    if (user.lmsUser?.id) {
      clearLocalStorage(user.lmsUser.id);
    }
    // FIXME: signOut が reject した場合は catch しておらず、呼び出し元も catch していない。
    //        store と localStorage は消えているのに Cookie だけ残る。
    await Auth.signOut();

    // FIXME: ログアウト前の store を参照しているため、リセットした直後にログアウト前のユーザー情報で Cookie が再設定される。
    //        実際は遷移後の refresh でリセットされるため実害は薄いが、直したほうが良い。
    setCookies();

    return;
  }, [dispatch, setCookies, user.lmsUser?.id]);

  const setIsChangePassword = (isChangePassword: boolean) => {
    dispatch({
      type: UserActionTypes.setIsChangePassword,
      payload: { isChangePassword: isChangePassword },
    });
  };

  const forgotPassword = async (email: string) => {
    await Auth.forgotPassword(email);
    setIsChangePassword(true);
  };

  return { socialLogin, logout, forgotPassword };
}

/** ロールとチームの切り替え。ログイン中なら localStorage に永続化してから store に反映する */
function useRoleAndTeamActions(
  user: UserState,
  dispatch: Dispatch,
): Pick<UseUser, 'changeRole' | 'changeRoleStudent' | 'changeTeamID' | 'removeTeamID'> {
  const changeRole = useCallback(
    (newRole: UserRole) => {
      if (user.lmsUser?.id) {
        // 再ログイン時、リロード時に権限が戻ってしまわないように永続化する
        setUserRoleToLocalStorage(newRole, user.lmsUser.id);
      }

      dispatch({
        type: UserActionTypes.changeRole,
        payload: { role: newRole },
      });
    },
    [dispatch, user.lmsUser?.id],
  );

  const changeRoleStudent = useCallback(() => {
    const roles = user.permissionList?.roles;
    if (!roles) return;

    const newRole = findStudentRole(roles);
    if (!newRole) return;

    changeRole(newRole);
  }, [user.permissionList?.roles, changeRole]);

  const changeTeamID = useCallback(
    (teamID: string) => {
      if (user.lmsUser?.id) {
        // 再ログイン時、リロード時に権限が戻ってしまわないように永続化する
        setUserTeamIDToLocalStorage(teamID, user.lmsUser.id);
      }

      dispatch({
        type: UserActionTypes.setTeamID,
        payload: { teamID: teamID },
      });
    },
    [dispatch, user.lmsUser?.id],
  );

  const removeTeamID = useCallback(() => {
    if (user.lmsUser?.id) {
      removeUserTeamIDToLocalStorage(user.lmsUser.id);
    }

    dispatch({
      type: UserActionTypes.setTeamID,
      payload: { teamID: null },
    });
  }, [dispatch, user.lmsUser?.id]);

  return { changeRole, changeRoleStudent, changeTeamID, removeTeamID };
}

/** ストアのユーザー情報から求める判定 (認可 / ロール切り替えの可否 / ログイン種別 / コーチング有無) */
function usePermissionChecks(
  user: UserState,
): Pick<
  UseUser,
  | 'permissionCheck'
  | 'permissionCheckToInstructor'
  | 'permissionCheckToCoach'
  | 'permissionCheckToStudent'
  | 'isSocialLoginUser'
  | 'hasCoaching'
> {
  const isSocialLoginUser = useCallback((): boolean => isGoogleLoginUser(user.cognitoUser), [user.cognitoUser]);

  const hasCoaching = (): boolean => hasCoachingCourse(user.lmsUser);

  const permissionCheck = useCallback(
    (functionType: string, permissionType: string): boolean => hasPermission(user.role, functionType, permissionType),
    [user.role],
  );

  const permissionCheckToInstructor = useCallback((): boolean => {
    if (user.role === UserRole.Instructor) return false;
    return hasInstructorRole(user.permissionList);
  }, [user.role, user.permissionList]);

  const permissionCheckToCoach = useCallback((): boolean => {
    if (user.role === UserRole.Coach) return false;
    return hasCoachRole(user.permissionList);
  }, [user.role, user.permissionList]);

  const permissionCheckToStudent = useCallback((): boolean => {
    if (UserRoleStudents.includes(user.role)) return false;
    return hasStudentRole(user.permissionList);
  }, [user.role, user.permissionList]);

  return {
    permissionCheck,
    permissionCheckToInstructor,
    permissionCheckToCoach,
    permissionCheckToStudent,
    isSocialLoginUser,
    hasCoaching,
  };
}

/** マウント時とユーザー情報が変わったときに、ユーザー属性と契約情報を GTM に送る */
function useGtmUserDataLayer(user: UserState) {
  useEffect(() => {
    if (!user.lmsUser) return;

    TagManager.dataLayer({
      dataLayer: {
        userId: user.lmsUser.id,
        isInstructor: user.lmsUser.isInstructor,
        isStudent: user.lmsUser.isStudent,
        pageView: {
          snsType: user.lmsUser.snsType,
          status: user.lmsUser.status,
          currentContract: user.contractsOfStripeSubscriptionForAnalytics,
          sex: user.lmsUser.personalInfo?.sex,
          createdDate: user.lmsUser.createdAt,
        },
      },
      dataLayerName: 'LMSDataLayer',
    });
  }, [user.lmsUser, user.contractsOfStripeSubscriptionForAnalytics]);
}

/** マウント時に、socialLoginでsessionStorageに退避したコールバックURLをストアに復元する */
function useRestoreSocialLoginCallbackURL(dispatch: Dispatch) {
  useEffect(() => {
    // ソーシャルログイン時のパラメーター引き回し対応
    // TODO: Hubのイベントがステージングで発火しない問題の原因が分かったらHubを使う方を戻す
    // @see https://docs.amplify.aws/lib/auth/social/q/platform/js/#setup-frontend
    // Hub.listen('auth', ({ payload: { event, data } }) => {
    //   if (event === 'customOAuthState') {
    //     dispatch(setSocialLoginCallbackURL({ callbackURL: data }));
    //   }
    // });
    try {
      const socialLoginCallbackURL = sessionStorageSupport.getItem('SOCIAL_LOGIN_CALLBACK_URL');
      if (socialLoginCallbackURL) {
        sessionStorageSupport.removeItem('SOCIAL_LOGIN_CALLBACK_URL');
        dispatch(setSocialLoginCallbackURL({ callbackURL: socialLoginCallbackURL }));
      }
    } catch {
      // sessionStorageが使えない場合は何もしない
    }
  }, [dispatch]);
}

/** GTM用Cookieの共通オプション */
function trackingCookieOptions() {
  return {
    expires: addDate(new Date(), 10, 0, 0),
    path: '/',
    domain: process.env.REACT_APP_COOKIE_DOMAIN,
    secure: true,
  };
}

/**
 * 権限と所属チームから現在のロールを決める。上から順に条件を満たしたものを返す。
 * 1. 権限情報かユーザーIDが無い → NotLogin
 * 2. localStorage に保存されたロール (Coach / Instructor / 受講生系) があり、その権限も持っている → 保存されたロール
 * 3. 所属チームがあり、チーム系の権限を持っている → チームのロール (このとき選択中のチームも決めてストアに反映する)
 * 4. Admin 権限を持っている → Admin 以外の権限があればその先頭、無ければ Admin
 * 5. いずれにも当てはまらない → 権限の先頭
 *
 * @param userID ログイン中ユーザーのID
 */
function resolveRoleAndSelectTeam(
  dispatch: Dispatch,
  permissionList: UserPermissionList | null | undefined,
  myTeams: MyTeamMemberFragment[] | undefined,
  userID: number | undefined,
): UserRole {
  if (!permissionList || !userID) return UserRole.NotLogin;

  // instructor,coach,student
  // ローカルストレージにログインロールが保存されていればそちらを優先
  // (手動でローカルストレージを変更するなどの不正ログインを防ぐため、userの状態もチェックする)
  const storedRole = getUserRoleFromLocalStorage(userID);
  if (storedRole === UserRole.Coach && hasCoachRole(permissionList)) {
    return storedRole;
  }
  if (storedRole === UserRole.Instructor && hasInstructorRole(permissionList)) {
    return storedRole;
  }
  if (storedRole && permissionList.roles.includes(storedRole) && hasStudentRole(permissionList)) {
    return storedRole;
  }

  // team
  if (myTeams && myTeams.length !== 0 && hasTeamRole(permissionList)) {
    const storedTeamID = getUserTeamIDFromLocalStorage(userID);

    if (storedTeamID) {
      const existTeam = myTeams.find((team) => team.teamID === storedTeamID);

      if (existTeam) {
        // ローカルストレージに保存してるteamIDが、最新の所属チーム情報に存在する場合
        dispatch({
          type: UserActionTypes.setTeamID,
          payload: { teamID: storedTeamID },
        });
        return resolveTeamRole(permissionList, existTeam);
      } else {
        // ローカルストレージに保存してるteamIDが、最新の所属チーム情報に存在しない場合
        // ローカルストレージに保存してるteamIDを削除
        removeUserTeamIDToLocalStorage(userID);
        // 最新の所属チームの一番新しいチームIDをstoreに保存
        // FIXME: currentTeam は所属情報 (メンバー) であってチームではないため、currentTeam.id はメンバーID。teamID を渡すべき。
        //        直後の refresh の setTeams で正しい値に上書きされるため実害はほぼないが、変数名も含めて直したほうが良い。
        const currentTeam = myTeams[myTeams.length - 1];
        dispatch({
          type: UserActionTypes.setTeamID,
          payload: { teamID: currentTeam.id },
        });
        return resolveTeamRole(permissionList, currentTeam);
      }
    } else {
      const currentTeam = myTeams[myTeams.length - 1];
      return resolveTeamRole(permissionList, currentTeam);
    }
  }

  // admin
  if (hasAdminRole(permissionList)) {
    const role = permissionList.roles.find((role) => role !== UserRole.Admin);
    // admin以外含まれる場合ははじめに取得できるRoleを設定
    if (role) return role;
    return UserRole.Admin;
  }

  // はじめに取得できるRoleを設定
  return permissionList.roles[0];
}

/**
 * チームの所属情報 (管理者 / 一般) と権限 (有料 / 無料) からチームのロールを決める。
 * @returns ロール。所属情報と権限が一致しない場合はNotLogin。
 */
function resolveTeamRole(permissionList: UserPermissionList, myTeam: MyTeamMemberFragment): UserRole {
  if (
    myTeam.role === TeamMemberRole.Admin &&
    permissionList.roles.includes(UserRole.TeamAdmin) &&
    permissionList.teamIDsForTeamAdmin.includes(myTeam.teamID)
  )
    return UserRole.TeamAdmin;
  if (
    myTeam.role === TeamMemberRole.Admin &&
    permissionList.roles.includes(UserRole.TeamAdminFree) &&
    !permissionList.teamIDsForTeamAdmin.includes(myTeam.teamID)
  )
    return UserRole.TeamAdminFree;
  if (
    myTeam.role === TeamMemberRole.General &&
    permissionList.roles.includes(UserRole.TeamMember) &&
    permissionList.teamIDs.includes(myTeam.teamID)
  )
    return UserRole.TeamMember;
  if (
    myTeam.role === TeamMemberRole.General &&
    permissionList.roles.includes(UserRole.TeamMemberFree) &&
    !permissionList.teamIDs.includes(myTeam.teamID)
  )
    return UserRole.TeamMemberFree;
  // 所属情報と権限が一致しない (異常値)
  return UserRole.NotLogin;
}

/** Admin 権限を持つかどうか (権限情報が無ければ false) */
function hasAdminRole(permissionList: UserPermissionList | null | undefined): boolean {
  if (!permissionList) return false;
  return permissionList.roles.includes(UserRole.Admin);
}

/** 講師権限を持つかどうか (権限情報が無ければ false) */
function hasInstructorRole(permissionList: UserPermissionList | null | undefined): boolean {
  if (!permissionList) return false;
  return permissionList.roles.includes(UserRole.Instructor);
}

/** コーチ権限を持つかどうか (権限情報が無ければ false) */
function hasCoachRole(permissionList: UserPermissionList | null | undefined): boolean {
  if (!permissionList) return false;
  return permissionList.roles.includes(UserRole.Coach);
}

/** 受講生系の権限 (UserRoleStudents のいずれか) を持つかどうか (権限情報が無ければ false) */
function hasStudentRole(permissionList: UserPermissionList | null | undefined): boolean {
  if (!permissionList) return false;
  return UserRoleStudents.some((studentRole) => permissionList.roles.includes(studentRole));
}

/** チーム系の権限 (UserRoleTeams のいずれか) を持つかどうか */
function hasTeamRole(permissionList: UserPermissionList): boolean {
  return UserRoleTeams.some((teamRole) => permissionList.roles.includes(teamRole));
}

/** API 呼び出しに使う Authorizationヘッダーに Cognito の IDトークンを設定する */
function setAuthorizationHeader(jwtToken: string): void {
  axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
}

/** 保有ロールから受講生系のロールを 1 つ選ぶ。UserRoleStudents の並び順で最初に一致したものを返す */
function findStudentRole(roles: UserRole[]): UserRole | undefined {
  return UserRoleStudents.find((studentRole) => roles.includes(studentRole));
}

/** ロールに対して、機能 (functionType) への操作 (permissionType) が UserPermissions で許可されているかどうか */
function hasPermission(role: UserRole, functionType: string, permissionType: string): boolean {
  const rolePermissions = (UserPermissions as Record<string, Record<string, string[]> | undefined>)[role];
  if (!rolePermissions) return false;

  const permissionTypes = rolePermissions[functionType];
  if (!permissionTypes) return false;

  return permissionTypes.includes(permissionType);
}

/** Google 認証のユーザーかどうか */
function isGoogleLoginUser(cognitoUser: CognitoUser | null): boolean {
  if (!cognitoUser) {
    return false;
  }
  // Facebook認証は廃止済みだが、Cognito上のUsernameは不変である。
  // 旧Facebook認証ユーザーがパスワード設定機能を使用できなくなってしまうため、Google認証のみを判定する。
  return /Google/.test(cognitoUser.getUsername());
}

/** コーチング付きの受講コースを 1 つでも持つかどうか */
function hasCoachingCourse(lmsUser: User | null): boolean {
  const courses = lmsUser?.student?.courses;
  if (courses !== undefined && courses.length > 0) {
    return courses.filter((c) => c?.coaching?.id).length > 0;
  }
  return false;
}
